import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "@/components/Logo";

const links = [
  { to: "/", label: "Home" },
  { to: "/make-a-payment", label: "Make a Payment" },
  { to: "/customer-care", label: "Get Help" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 border-b border-slate-900/10 bg-white/80 backdrop-blur-xl backdrop-saturate-150"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" aria-label="Concorde Auto Acceptance home">
          <Logo big />
        </Link>

        <nav className="hidden md:flex items-center gap-9" data-testid="nav-desktop-links">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide transition-colors duration-300 hover:text-slate-900 ${
                  isActive ? "text-ember" : "text-slate-600"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <a
            href="https://my.blytzpay.com/#/merchant/7acdb786-1f15-4eca-aff5-f4b1fea02516/login"
            data-testid="nav-payment-cta"
            className="inline-flex items-center gap-2 rounded-full bg-ember px-4 py-2 text-xs sm:px-6 sm:py-2.5 sm:text-sm font-bold text-white transition-all duration-300 hover:bg-tangerine hover:shadow-[0_0_28px_rgba(255,87,34,0.45)]"
          >
            Pay Now
            <ArrowUpRight size={16} />
          </a>
          <button
            data-testid="nav-mobile-menu-button"
            className="md:hidden text-slate-800 p-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            key={location.pathname}
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden border-t border-slate-900/10 bg-white/95 backdrop-blur-xl"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `font-display text-2xl font-bold ${isActive ? "text-ember" : "text-slate-800"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <a
                href="https://my.blytzpay.com/#/merchant/7acdb786-1f15-4eca-aff5-f4b1fea02516/login"
                data-testid="nav-mobile-payment-cta"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-ember px-8 py-4 font-bold text-white"
              >
                Pay Now <ArrowUpRight size={18} />
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
